import { NIL as NIL_UUID } from 'uuid';
import { DirectoryTabs } from './Directory.enum';
import type { tDirectoryFilterInput } from './Directory.query';

/**
 * The part of the directory.v1 `Query` the Directory can state exactly
 * (bff-web `directorycontract/schema.json`, `$defs.Query`).
 */
export type tMatchingQuery = {
  active?: boolean;
  in_inbox?: boolean;
  funnel_uuid?: string;
  stage_uuid?: string;
  user_uuid?: string;
  status_name?: string;
  message?: { read: boolean };
};

/** A whole match as the Directory holds it: the query, its total and, when known, how many are archived. */
export type tMatchingSelection = {
  query: tMatchingQuery;
  total: number;
  archivedCount?: number;
};

export type tMatchingInput = tDirectoryFilterInput & {
  /** The tag chip filters the loaded rows only, not the listing's predicate. */
  activeTagFilter?: unknown;
};

/**
 * The view's predicate as a directory.v1 matching query, so "select all that
 * match" captures exactly the leads the list counts. Null when the view uses
 * a filter the contract states differently: the list's search is name-only
 * while `find` also matches phone and email; tags, dates and channel
 * filters use other formats or ids. A wrong mapping would archive or move
 * leads the user never saw, so those views do not offer the option.
 */
export function toMatchingQuery(input: tMatchingInput): tMatchingQuery | null {
  const unsupported =
    Boolean(input.search?.trim()) ||
    Boolean(input.activeTagFilter) ||
    Boolean(
      input.messageReadFilter &&
      input.messageReadFilter !== 'read' &&
      input.messageReadFilter !== 'unread',
    ) ||
    Boolean(input.dateRange?.some(Boolean)) ||
    Boolean(input.integrationFilter) ||
    Boolean(input.tagsFilter?.length);
  if (unsupported) return null;

  const owner = input.assignedUserFilters;
  if (owner && (!owner.authId || (owner.authId !== 'NONE' && !owner.uuid)))
    return null;
  const status = input.statusFilter?.name;
  if (input.statusFilter && (typeof status !== 'string' || !status.trim()))
    return null;

  const inInbox = input.selectedFunnelForFilter === NIL_UUID;
  return {
    ...(input.selectedTab !== DirectoryTabs.ALL && {
      active: input.selectedTab === DirectoryTabs.ACTIVE,
    }),
    ...(inInbox && { in_inbox: true }),
    ...(!inInbox &&
      input.selectedFunnelForFilter !== 'all' && {
        funnel_uuid: input.selectedFunnelForFilter,
      }),
    ...(!inInbox &&
      input.selectedStageForFilter !== 'all' && {
        stage_uuid: input.selectedStageForFilter,
      }),
    ...(owner && { user_uuid: owner.authId === 'NONE' ? 'NONE' : owner.uuid }),
    ...(status && { status_name: status }),
    ...(input.messageReadFilter && {
      message: { read: input.messageReadFilter === 'read' },
    }),
  };
}
